import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getNewsDetail } from './NewsApi';

const SERVER = process.env.REACT_APP_SERVER;

const colors = {
    text: '#1a1a1a',
    primary: '#005a8d',
    primaryDark: '#004870',
    primaryLight: '#f5f9fc',
    border: '#e7edf2',
    muted: '#555555',
};

const CATEGORY_LABEL = {
    notice: '공지사항',
    press: '보도자료',
};

const CATEGORY_COLOR = {
    notice: 'primary',
    press: 'success',
};

const normalizeNews = (payload) => {
    if (Array.isArray(payload)) return payload[0] || null;
    return payload || null;
};

const getNoticeFileUrl = (fileName) => {
    if (!fileName) return '';
    if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
    if (fileName.startsWith('/api/')) return `${SERVER}${fileName}`;
    return `${SERVER}/api/upload/noticeFiles/${fileName}`;
};

const normalizeContentHtml = (html = '') => {
    if (!html) return '';

    return html.replace(
        /(src=["'])\/api\/upload\/noticeFiles\//g,
        `$1${SERVER}/api/upload/noticeFiles/`
    );
};
const formatDateString = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
};

function NewsDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;

        const fetchNews = async () => {
            try {
                const res = await getNewsDetail(id);
                if (mounted) {
                    setNews(normalizeNews(res.data));
                    setError('');
                }
            } catch (err) {
                console.error('API 호출 실패 상세 원인:', err);
                if (mounted) {
                    setError('소식을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchNews();

        return () => {
            mounted = false;
        };
    }, [id]);

    return (
        <Box sx={{ bgcolor: '#ffffff', color: colors.text, minHeight: '100vh' }}>
            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
                <Container maxWidth={false} sx={{ maxWidth: 990 }}>
                    {loading && (
                        <Box
                            sx={{
                                minHeight: 260,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress sx={{ color: colors.primary }} />
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && news && (
                        <Paper
                            variant="outlined"
                            sx={{ p: { xs: 3, md: 5 }, borderColor: colors.border, borderRadius: 4 }}
                        >
                            <Stack spacing={1.5} sx={{ mb: 3 }}>
                                <Chip
                                    label={CATEGORY_LABEL[news.category] || '알림'}
                                    color={CATEGORY_COLOR[news.category] || 'default'}
                                    size="small"
                                    sx={{ fontWeight: 800, alignSelf: 'flex-start' }}
                                />
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 800, wordBreak: 'keep-all' }}
                                >
                                    {news.title || '제목 미정'}
                                </Typography>
                                <Typography sx={{ color: colors.muted, fontSize: '0.9rem' }}>
                                    {formatDateString(news.created_at)}
                                </Typography>
                            </Stack>

                            <Divider sx={{ mb: 3 }} />

                            <Typography
                                component="div"
                                sx={{
                                    lineHeight: 1.8,
                                    wordBreak: 'keep-all',
                                    '& img': {
                                        width: '100%',
                                        maxWidth: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        borderRadius: 2,
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: normalizeContentHtml(news.content),
                                }}
                            />

                            {news.file_name && (
                                <Box sx={{ my: 3 }}>
                                    {news.file_name.toLowerCase().endsWith('.pdf') ? (
                                        <Button
                                            variant="outlined"
                                            href={getNoticeFileUrl(news.file_name)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            첨부파일 보기
                                        </Button>
                                    ) : (
                                        <Box
                                            component="img"
                                            src={getNoticeFileUrl(news.file_name)}
                                            alt={news.file_name}
                                            sx={{ width: '100%', borderRadius: 2, display: 'block' }}
                                        />
                                    )}
                                </Box>
                            )}
                        </Paper>
                    )}

                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/news')}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontWeight: 800,
                                color: colors.primary,
                                borderColor: colors.border,
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: colors.primary,
                                    bgcolor: colors.primaryLight,
                                },
                            }}
                        >
                            목록으로 돌아가기
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

export default NewsDetailPage;
